import { exportToBlob } from "../../packages/utils/export";
import { NonDeletedExcalidrawElement } from "../../packages/excalidraw/element/types";
import { AppState, BinaryFiles } from "../../packages/excalidraw/types";

/**
 * Client-generated My Files thumbnails.
 *
 * Generation (`generateThumbnailBlob`/`uploadThumbnail`) runs from the
 * autosave success path (see `useCloudAutosave.ts`), strictly after a
 * successful scene save, debounced via `thumbnailTrigger.ts`. Every step is
 * best-effort: a failure here must never surface to the editor, so callers
 * only ever see a resolved promise (see `generateAndUploadThumbnail`).
 *
 * Fetching (`fetchThumbnailBlob`) is used by My Files cards, which are an
 * owner-only surface: an `<img src>` can't send an Authorization header, so
 * a private file's thumbnail (owner-only, like its scene) is fetched as a
 * blob with the caller's access token and turned into an object URL by the
 * caller, mirroring the existing authenticated-GET pattern already used for
 * cloud scenes (`cloudScene.ts#loadSceneFromCloud`).
 */

const SERVER_URL = import.meta.env.VITE_APP_BACKEND_URL || "/api";
const THUMBNAIL_BACKEND_URL = `${SERVER_URL}/thumbnail`;

export const THUMBNAIL_MAX_WIDTH = 320;
export const THUMBNAIL_MAX_HEIGHT = 200;

/**
 * Base per-file thumbnail endpoint, with the file id safely encoded for use
 * in a URL path segment.
 */
const thumbnailEndpoint = (fileId: string): string =>
  `${THUMBNAIL_BACKEND_URL}/${encodeURIComponent(fileId)}`;

/**
 * Cache-busted thumbnail URL for a file, using its `updated_at` as the
 * version token — a fresh save produces a new `updated_at`, so the next My
 * Files visit fetches the new image instead of a stale cached one.
 */
export const thumbnailUrl = (fileId: string, updatedAt: string): string =>
  `${thumbnailEndpoint(fileId)}?v=${encodeURIComponent(updatedAt)}`;

/**
 * Render a fit-to-content PNG thumbnail (device-pixel-ratio 1) scaled down to
 * fit within THUMBNAIL_MAX_WIDTH x THUMBNAIL_MAX_HEIGHT, preserving aspect
 * ratio. Small content is never upscaled. Returns null for an empty scene (no
 * non-deleted elements) or on any export failure — callers should keep the
 * existing thumbnail/placeholder in that case, never surface an error.
 */
export const generateThumbnailBlob = async (
  elements: readonly NonDeletedExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
): Promise<Blob | null> => {
  if (elements.length === 0) {
    return null;
  }
  try {
    return await exportToBlob({
      elements,
      appState: { ...appState, exportBackground: true },
      files,
      getDimensions: (width, height) => {
        const scale = Math.min(
          THUMBNAIL_MAX_WIDTH / width,
          THUMBNAIL_MAX_HEIGHT / height,
          1, // never upscale small content
        );
        return {
          width: Math.max(1, Math.round(width * scale)),
          height: Math.max(1, Math.round(height * scale)),
          scale,
        };
      },
    });
  } catch (error) {
    console.warn("Thumbnail export failed", error);
    return null;
  }
};

/**
 * PUT a thumbnail blob to the storage endpoint. Owner-only (the endpoint
 * enforces this); best-effort — any failure (size cap, offline, auth) is
 * swallowed, matching "generation never blocks/breaks editing". Returns
 * whether the upload actually landed (a 2xx), so the caller only records the
 * cooldown for a thumbnail that was genuinely persisted.
 */
export const uploadThumbnail = async (
  fileId: string,
  blob: Blob,
  accessToken: string,
): Promise<boolean> => {
  if (!accessToken) {
    return false;
  }
  try {
    const response = await fetch(thumbnailEndpoint(fileId), {
      method: "PUT",
      body: blob,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "image/png",
      },
    });
    if (!response.ok) {
      console.warn(`Thumbnail upload failed (status ${response.status})`);
      return false;
    }
    return true;
  } catch (error) {
    console.warn("Thumbnail upload failed", error);
    return false;
  }
};

/**
 * Best-effort DELETE of a file's thumbnail (owner-only, enforced by the
 * endpoint). Used when a save persists an EMPTY scene — the file has no
 * content, so its old preview must not linger (both stale and a privacy leak
 * if the removed content was sensitive). Never rejects; returns whether the
 * delete landed (a 2xx, notably the 204 the endpoint answers with).
 */
export const deleteThumbnail = async (
  fileId: string,
  accessToken: string,
): Promise<boolean> => {
  if (!accessToken) {
    return false;
  }
  try {
    const response = await fetch(thumbnailEndpoint(fileId), {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (!response.ok) {
      console.warn(`Thumbnail delete failed (status ${response.status})`);
      return false;
    }
    return true;
  } catch (error) {
    console.warn("Thumbnail delete failed", error);
    return false;
  }
};

/**
 * Generate + upload a thumbnail for `fileId`. Fire-and-forget from the
 * caller's perspective: this never rejects, and skips silently for an empty
 * scene or any failure along the way. Returns whether a blob was produced AND
 * uploaded, so the caller records the rate-limit cooldown only for a real,
 * persisted generation (a no-op/empty/failed attempt must not burn the window
 * and suppress the next real thumbnail).
 */
export const generateAndUploadThumbnail = async (
  fileId: string,
  elements: readonly NonDeletedExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
  accessToken: string,
): Promise<boolean> => {
  try {
    const blob = await generateThumbnailBlob(elements, appState, files);
    if (!blob) {
      return false;
    }
    return await uploadThumbnail(fileId, blob, accessToken);
  } catch (error) {
    console.warn("Thumbnail generation failed", error);
    return false;
  }
};

/**
 * Authenticated GET of a file's thumbnail as a raw Blob (used both by My
 * Files cards to build an object URL, and by `duplicateFile` to copy the
 * thumbnail to a new id). Never throws: returns null on a 404/anon/error, so
 * callers can fall back to the placeholder without special-casing failures.
 */
export const fetchThumbnailBlob = async (
  fileId: string,
  updatedAt: string,
  accessToken: string,
): Promise<Blob | null> => {
  if (!accessToken) {
    return null;
  }
  try {
    const response = await fetch(thumbnailUrl(fileId, updatedAt), {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) {
      return null;
    }
    return await response.blob();
  } catch {
    return null;
  }
};

/**
 * `fetchThumbnailBlob` + `URL.createObjectURL`, for direct use as an `<img
 * src>` on My Files cards. Returns null on any failure/absence (caller falls
 * back to the placeholder). Callers own the returned URL's lifetime and
 * should `URL.revokeObjectURL` it when no longer displayed.
 */
export const fetchThumbnailObjectUrl = async (
  fileId: string,
  updatedAt: string,
  accessToken: string,
): Promise<string | null> => {
  const blob = await fetchThumbnailBlob(fileId, updatedAt, accessToken);
  if (!blob) {
    return null;
  }
  return URL.createObjectURL(blob);
};
